import MarketStrategies, { MarketCodeISO2 } from "../strategies/market";
import { PsychologistProfile } from "./types";

// Define the validation result type for password
interface PasswordValidationResult {
  isValid: boolean;
  message: string | null;
}

interface Comment {
  comment: string;
}

type ValidationInput =
  | { type: "email"; value: string }
  | { type: "phonenumber"; value: string }
  | { type: "adeli"; value: string }
  | { type: "appVersion"; value: string }
  | { type: "password"; value: string }
  | { type: "descriptions"; value: { description: string } }
  | { type: "comments"; value: Comment[] };

const validatePhoneNumber = (phonenumber: string): boolean =>
  !!phonenumber && !!phonenumber.match(/^\+[0-9]{8,}$/);

const validateEmail = (email: string): boolean => {
  if (email.length === 0) {
    return false;
  }
  if (email.includes(" ")) {
    return false;
  }
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return !!email.match(emailRegex);
};

const validatePassword = (password: string): PasswordValidationResult => {
  if (password.length < 6) {
    return { isValid: false, message: "Password not long enough" };
  }
  if (!password.match(/(?=.*[a-z])/)) {
    return {
      isValid: false,
      message: "Password must have lowercase characters",
    };
  }
  if (!password.match(/(?=.*[A-Z])/)) {
    return {
      isValid: false,
      message: "Password must have uppercase characters",
    };
  }
  if (!password.match(/(?=.*\d)/)) {
    return { isValid: false, message: "Password must have numeric characters" };
  }
  if (!password.match(/(?=.*\W)/)) {
    return { isValid: false, message: "Password must have symbol characters" };
  }
  return { isValid: true, message: null };
};

const validateAdeli = (adeli: string): boolean =>
  !!adeli.replace(/\s/g, "").match(/^[a-zA-Z0-9]{9,10}$/);

const validateDescription = (description: { description: string }): boolean =>
  !!description?.description;

const validateAppVersion = (version: string): boolean => {
  const versionNums = version.split(".");
  return versionNums.every((item) => item.match(/^\d+$/));
};

export default function validate(
  input: ValidationInput,
): boolean | PasswordValidationResult {
  switch (input.type) {
    case "email":
      return validateEmail(input.value);
    case "phonenumber":
      return validatePhoneNumber(input.value);
    case "adeli":
      return validateAdeli(input.value);
    case "appVersion":
      return validateAppVersion(input.value);
    case "descriptions":
      return validateDescription(input.value);
    case "password":
      return validatePassword(input.value);
    case "comments":
      return input.value.every(
        (comment) =>
          !comment.comment ||
          comment.comment.length === 0 ||
          comment.comment.length >= 5,
      );
  }
}

export function validatePsychologist(
  psychologist: PsychologistProfile,
  market: string,
  defaultLanguageId: number | undefined,
): { psychologistValidation: string[] } {
  const REQUIRED_FIELDS: (keyof PsychologistProfile | string)[] = [
    "phone",
    "gender",
    "headlineEnum",
    "descriptions",
    "comments",
    "languages",
    "specialities",
    "thumbnail",
    "bigImage",
    "absenceStatus",
    ...(MarketStrategies[market as MarketCodeISO2].psychologist.hasRequiredAdeli
      ? ["hsa"]
      : []),
  ];

  const psychologistKeys = Object.keys(psychologist).filter(
    (key) => REQUIRED_FIELDS.indexOf(key) !== -1,
  ) as (keyof PsychologistProfile)[];

  const returnData = {
    psychologistValidation: [] as string[],
  };

  returnData.psychologistValidation = psychologistKeys.filter(
    (key) =>
      !psychologist[key] ||
      (key === "languages" && psychologist.languages.length === 0) ||
      (key === "specialities" && psychologist.specialities.length === 0),
  );

  if (
    psychologistKeys.includes("phone") &&
    !validate({ type: "phonenumber", value: psychologist.phone! })
  ) {
    returnData.psychologistValidation.push("phone");
  }
  if (
    psychologistKeys.includes("hsa") &&
    !validate({ type: "adeli", value: psychologist.hsa! })
  ) {
    returnData.psychologistValidation.push("hsa");
  }

  const defaultDescription = psychologist.descriptions?.find(
    (description) =>
      description.countryLanguage.languageId === defaultLanguageId,
  );

  if (
    psychologistKeys.includes("descriptions") &&
    !validate({ type: "descriptions", value: defaultDescription! })
  ) {
    returnData.psychologistValidation.push("descriptions");
  }
  if (
    psychologistKeys.includes("comments") &&
    !validate({ type: "comments", value: psychologist.comments! })
  ) {
    returnData.psychologistValidation.push("comments");
  }
  return returnData;
}

export function validateNameUpdate(
  names: { [key: string]: string },
  t: (key: string) => string,
): { nameValidation: { field: string; message: string }[] } {
  const REQUIRED_FIELDS = ["firstName", "lastName"];
  const nameKeys = Object.keys(names).filter(
    (key) => REQUIRED_FIELDS.indexOf(key) !== -1,
  );
  const returnData = {
    nameValidation: [] as { field: string; message: string }[],
  };

  nameKeys.forEach((key) => {
    const name = names[key];

    // Check if name is empty
    if (!name) {
      returnData.nameValidation.push({
        field: key,
        message: t("MISSING_NAME_ERROR"),
      });
      return;
    }

    // Check for emojis
    if (name.match(/\p{Extended_Pictographic}/gu)) {
      returnData.nameValidation.push({
        field: key,
        message: t("INVALID_NAME_EMOJIS_ERROR"),
      });
      return;
    }

    // Check for numbers
    if (name.match(/[0-9]/)) {
      returnData.nameValidation.push({
        field: key,
        message: t("INVALID_NAME_NUMBERS_ERROR"),
      });
      return;
    }

    // Check for invalid characters (only allow letters, spaces, hyphens, apostrophes)
    if (
      name.match(/[^a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF\s'-]/u)
    ) {
      returnData.nameValidation.push({
        field: key,
        message: t("INVALID_NAME_SPECIAL_CHARACTERS_ERROR"),
      });
      return;
    }

    // Check for multiple consecutive separators
    if (name.match(/[-'\s]{2,}/)) {
      returnData.nameValidation.push({
        field: key,
        message: t("INVALID_NAME_MULTIPLE_SEPARATORS_ERROR"),
      });
      return;
    }

    if (name === name.toLowerCase()) {
      returnData.nameValidation.push({
        field: key,
        message: t("INVALID_NAME_ALL_LOWERCASE_ERROR"),
      });
      return;
    }

    // Check for all uppercase (but allow single letters)
    if (name === name.toUpperCase() && name.length > 1) {
      returnData.nameValidation.push({
        field: key,
        message: t("INVALID_NAME_ALL_UPPERCASE_ERROR"),
      });
      return;
    }
  });

  return returnData;
}
